import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';

/**
 * Activity — one bookable slot on one product, at one time.
 *
 * Read-only, like the old page: an activity is produced by a crawling or by a
 * product's schedule, not edited by hand.
 *
 * `isShadowed` and `isBuried` are why anyone opens this page — they are the two
 * reasons a slot exists but does not appear for sale — so they are stated as
 * plain sentences rather than as two booleans a reader has to translate.
 *
 * The signups list is the old page's, as a table rather than a bullet list: it is
 * the answer to "who took this slot".
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const ActivityInternal = ({ api }) => {
  const { activityId } = useParams();

  const [activity, setActivity] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.activities.getActivity(activityId);
      if (!data || data.success === false || !(data.id || data.Id)) {
        setFailed(true);
        return;
      }
      setActivity(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, activityId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/activities">← All activities</BackToList>
        <div className="next-note next-note--error">
          Could not load this activity.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!activity) return <div className="next-page next-muted">Loading…</div>;

  const signups = Array.isArray(activity.signups) ? activity.signups : [];

  const facts = [
    ['Title', activity.title || <span className="next-muted">—</span>],
    ['Starts at', fmtDate(activity.startsAt)],
    ['Ends at', fmtDate(activity.endsAt)],
    ['Spot index', activity.spotIndex ?? <span className="next-muted">—</span>],
    ['Product id', activity.productId
      ? <Guid value={activity.productId} href={`/product/details/${activity.productId}`} />
      : <span className="next-muted">—</span>],
    ['Facility id', activity.facilityId
      ? <Guid value={activity.facilityId} to={`/next/facility/${activity.facilityId}`} />
      : <span className="next-muted">—</span>],
    ['Crawling id', activity.crawlingId
      ? <Guid value={activity.crawlingId} href={`/crawling/${activity.crawlingId}`} />
      : <span className="next-muted">—</span>],
    ['External activity id', activity.externalActivityId
      ? <Guid value={activity.externalActivityId} to={`/next/external-activity/${activity.externalActivityId}`} />
      : <span className="next-muted">—</span>],
    ['External reference', activity.externalReference || <span className="next-muted">—</span>],
    ['Created at', fmtDate(activity.createdAt)]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/activities">← All activities</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{activity.title || 'Activity'}</h1>
          {activity.isShadowed && <span className="next-pill next-pill--warn">Shadowed</span>}
          {activity.isBuried && <span className="next-pill next-pill--off">Buried</span>}
        </div>
        <span className="next-detail__id">{activity.id || activityId}</span>
      </header>

      {/* The two flags in words: they are the reason a slot is missing from the
          site, and "isShadowed: true" does not say that. */}
      {(activity.isShadowed || activity.isBuried) && (
        <div className="next-note next-note--warn">
          {activity.isShadowed && <div>Shadowed — hidden from the site, but still bookable by a direct link.</div>}
          {activity.isBuried && <div>Buried — no longer offered at all.</div>}
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Activity</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Signups</h2>
              <span className="next-muted">
                {signups.length} on this slot
              </span>
            </div>

            {signups.length === 0 ? (
              <p className="next-table__note next-muted">Nobody has taken this slot.</p>
            ) : (
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Signup</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signups.map(signup => (
                      <tr key={signup.id}>
                        <td data-label="">
                          <Guid value={signup.id} to={`/next/signup/${signup.id}`} />
                        </td>
                        <td data-label="Status">
                          {signup.status
                            ? <StatusPill status={signup.status}>{signup.status}</StatusPill>
                            : <span className="next-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                activity.facilityId && {
                  label: 'Facility',
                  to: `/next/facility/${activity.facilityId}`
                },
                activity.productId && {
                  label: 'Other activities',
                  hint: 'on this product',
                  to: `/next/activities?productId=${activity.productId}`
                },
                activity.facilityId && {
                  label: 'Activities here',
                  hint: 'same facility',
                  to: `/next/activities?facilityId=${activity.facilityId}`
                },
                activity.crawlingId && {
                  label: 'From this crawling',
                  to: `/next/activities?crawlingId=${activity.crawlingId}`
                },
                activity.productId && {
                  label: 'Product',
                  href: `/product/details/${activity.productId}`
                },
                activity.externalActivityId && {
                  label: 'External activity',
                  to: `/next/external-activity/${activity.externalActivityId}`
                }
              ]}
            />
          </section>

          {/* No Actions panel: the old page has none, and an activity is created
              by a crawling or a schedule rather than edited here. */}
        </aside>
      </div>
    </div>
  );
};

ActivityInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Activity = withApi(ActivityInternal);

Activity.requiresLogin = true;

export default Activity;
